import { CheckCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { WorkorderRow, WorkorderType } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { SlaBadge } from '../components/SlaBadge';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待受理', color: 'orange' },
  ASSIGNED: { text: '已指派', color: 'processing' },
  PROCESSING: { text: '处理中', color: 'processing' },
  WAIT_PART: { text: '待配件', color: 'gold' },
  FINISHED: { text: '待验收', color: 'blue' },
  VERIFIED: { text: '已验收', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' }
};

const TYPE_META: Record<string, { text: string; color: string }> = {
  REPAIR: { text: '维修', color: 'volcano' },
  INSTALL: { text: '安装', color: 'geekblue' },
  COMPLAINT: { text: '投诉', color: 'red' },
  CONSULT: { text: '咨询', color: 'cyan' }
};

interface CreateValues {
  orderType: WorkorderType;
  urgentLevel: 'NORMAL' | 'URGENT' | 'EMERGENCY';
  materialId?: string;
  faultDesc: string;
}

/**
 * 报修 / 售后工单（docs/01 §6.2-14）：
 * 医院端发起维修并跟踪进度；受理、指派、处理在运营端「售后工单」完成，
 * 完成后医院端验收（VERIFIED）与关闭（CLOSED），形成 24h 响应闭环。
 */
export default function WorkorderList() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<CreateValues>();

  const list = useInfiniteQuery({
    queryKey: ['workorder', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.workorder.list({ lastId: pageParam ?? undefined, size: PAGE_SIZE, status: status === 'ALL' ? undefined : status }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['workorder', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.workorder.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const materials = useQuery({
    queryKey: ['material', 'options'],
    queryFn: () => hq.material.list({ size: 500 }),
    staleTime: 10 * 60 * 1000,
    enabled: createOpen
  });

  const create = useMutation({
    mutationFn: (values: CreateValues) =>
      hq.workorder.create({
        orderType: values.orderType,
        urgentLevel: values.urgentLevel,
        materialId: values.materialId,
        faultDesc: values.faultDesc
      }),
    onSuccess: async (result) => {
      message.success(`工单已创建：${result.bizNo}，SLA ${result.slaHours} 小时`);
      setCreateOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['workorder'] });
      setCurrentId(result.id);
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '建单失败')
  });

  const handle = useMutation({
    mutationFn: (action: 'VERIFIED' | 'CLOSED' | 'CANCELED') =>
      hq.workorder.handle(currentId ?? '', { action, solution: action === 'CLOSED' ? '医院端确认关闭' : undefined }),
    onSuccess: async (result) => {
      message.success(`工单状态已更新为 ${STATUS_META[result.status]?.text ?? result.status}`);
      await queryClient.invalidateQueries({ queryKey: ['workorder'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const current = detail.data;

  const columns: ColumnsType<WorkorderRow> = [
    {
      title: '工单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    {
      title: '类型',
      dataIndex: 'orderType',
      width: 90,
      render: (value: string) => {
        const meta = TYPE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '故障描述', dataIndex: 'faultDesc', ellipsis: { showTitle: false }, render: ellipsisCell('faultDesc') },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: 'SLA',
      dataIndex: 'expectFinishAt',
      width: 160,
      render: (value: string | null, row) =>
        ['CLOSED', 'CANCELED', 'VERIFIED'].includes(row.status) ? (
          <Tag color={row.isOvertime ? 'red' : 'green'}>{row.isOvertime ? '已超时' : '按期'}</Tag>
        ) : (
          <SlaBadge deadline={value} />
        )
    },
    { title: '处理人', dataIndex: 'assignName', width: 110, render: (value: string | null) => value || '-' },
    { title: '提交时间', dataIndex: 'createAt', width: 150, render: (value: string) => formatDisplayTime(value) }
  ];

  return (
    <Card
      className="page-card"
      title="报修工单"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            发起报修
          </Button>
        </Space>
      }
    >
      <Segmented
        value={status}
        onChange={(value) => setStatus(String(value))}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '待受理', value: 'NEW' },
          { label: '处理中', value: 'PROCESSING' },
          { label: '待验收', value: 'FINISHED' },
          { label: '已关闭', value: 'CLOSED' }
        ]}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="工单加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<WorkorderRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: list.isError ? '加载失败，请重试' : '暂无工单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title="发起报修 / 售后"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="提交"
        cancelText="取消"
        confirmLoading={create.isPending}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<CreateValues>
          form={form}
          layout="vertical"
          initialValues={{ orderType: 'REPAIR', urgentLevel: 'NORMAL' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="orderType" label="工单类型" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'REPAIR', label: '维修' },
                { value: 'INSTALL', label: '安装' },
                { value: 'COMPLAINT', label: '投诉' },
                { value: 'CONSULT', label: '咨询' }
              ]}
            />
          </Form.Item>
          <Form.Item name="materialId" label="关联物资（可选）">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={materials.isLoading}
              placeholder="搜索物资名称 / 编码"
              options={(materials.data?.items ?? []).map((row) => ({
                value: row.id,
                label: `${row.materialName ?? row.id}${row.materialCode ? `（${row.materialCode}）` : ''}`
              }))}
            />
          </Form.Item>
          <Form.Item name="urgentLevel" label="紧急程度" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="NORMAL">普通（24h）</Radio.Button>
              <Radio.Button value="URGENT">加急（8h）</Radio.Button>
              <Radio.Button value="EMERGENCY">应急（2h）</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="faultDesc"
            label="故障描述"
            rules={[{ required: true, min: 5, message: '至少 5 个字，便于客服判断' }]}
          >
            <Input.TextArea rows={4} maxLength={500} showCount placeholder="如：输液泵报警无法启动，已停止使用" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={current?.bizNo ?? '工单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={720}
        extra={
          current ? (
            <Space>
              {current.status === 'FINISHED' ? (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={handle.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: '确认验收',
                      content: '验收后工单进入已验收状态，仍可关闭；如维修不合格请联系客服重新处理。',
                      okText: '验收通过',
                      onOk: () => handle.mutateAsync('VERIFIED')
                    })
                  }
                >
                  验收
                </Button>
              ) : null}
              {['VERIFIED', 'FINISHED'].includes(current.status) ? (
                <Button
                  loading={handle.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: '关闭工单',
                      content: '关闭后不可再流转，确认问题已解决。',
                      okText: '关闭',
                      onOk: () => handle.mutateAsync('CLOSED')
                    })
                  }
                >
                  关闭
                </Button>
              ) : null}
              {current.status === 'NEW' ? (
                <Button
                  danger
                  loading={handle.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: '取消工单',
                      content: '仅待受理状态可取消。',
                      okText: '取消工单',
                      okButtonProps: { danger: true },
                      onOk: () => handle.mutateAsync('CANCELED')
                    })
                  }
                >
                  取消
                </Button>
              ) : null}
            </Space>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : current ? (
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_META[current.status]?.color}>{STATUS_META[current.status]?.text ?? current.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="类型">{TYPE_META[current.orderType]?.text ?? current.orderType}</Descriptions.Item>
            <Descriptions.Item label="SLA">{current.slaHours} 小时</Descriptions.Item>
            <Descriptions.Item label="应完成">{formatDisplayTime(current.expectFinishAt)}</Descriptions.Item>
            <Descriptions.Item label="处理人">{current.assignName || '-'}</Descriptions.Item>
            <Descriptions.Item label="完成时间">{formatDisplayTime(current.finishAt)}</Descriptions.Item>
            <Descriptions.Item label="关联物资">{current.materialName || '-'}</Descriptions.Item>
            <Descriptions.Item label="费用">{formatAmount(current.feeAmount)}</Descriptions.Item>
            <Descriptions.Item label="故障描述" span={2}>
              {current.faultDesc || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="处理方案" span={2}>
              {current.solution || '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>
    </Card>
  );
}
